import { zodResolver } from '@hookform/resolvers/zod'
import {
  Button as MuiButton,
  Checkbox as MuiCheckbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material'
import { Link } from '@fluentui/react-components'
import { CheckmarkCircle24Filled, CalendarLtr24Regular } from '@fluentui/react-icons'
import { format, startOfMonth } from 'date-fns'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { GRAPH_CONFIG } from '../../config/graphConfig'
import {
  BOOKING_TIME_ZONES,
  getInitialBookingTimeZone,
} from '../../config/timeZones'
import { useAvailability } from '../../hooks/useAvailability'
import { useBookingBusiness } from '../../hooks/useBookingBusiness'
import { useCreateAppointment } from '../../hooks/useCreateAppointment'
import { useServices } from '../../hooks/useServices'
import { useStaffMembers } from '../../hooks/useStaffMembers'
import type {
  AvailableSlot,
  BookingAppointment,
  BookingService,
} from '../../types/bookings'
import {
  createEmptyCustomerDetails,
  formatAppointmentDateTime,
  formatDuration,
  getBusinessOpenDays,
  getServiceQuestions,
  hasSmsSupport,
  normalizeCustomerPhone,
  toDateTimeTimeZone,
} from '../../utils/dateTime'
import { postContact } from '../../../services/ApiService'
import { ErrorBanner } from '../shared/ErrorBanner'
import { LoadingSpinner } from '../shared/LoadingSpinner'
import { CalendarPicker } from '../steps/DateTimeStep/CalendarPicker'
import { TimeSlotGrid } from '../steps/DateTimeStep/TimeSlotGrid'
import styles from './SchedulingPage.module.scss'

// ── Schema ──────────────────────────────────────────────────────────────────

const detailsSchema = z.object({
  name: z.string().min(2, 'Full name is required'),
  email: z.string().email('Enter a valid email address'),
  phone: z.string().min(6, 'Phone number is required'),
  company: z.string().min(2, 'Company name is required'),
  notes: z.string().max(500, 'Notes must be 500 characters or fewer').optional(),
  smsOptIn: z.boolean(),
  questions: z.record(z.string(), z.string()),
})

type DetailsFormValues = z.infer<typeof detailsSchema>

// ── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(displayName: string): string {
  return displayName
    .split(' ')
    .map((w) => w[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

// ── Main Component ───────────────────────────────────────────────────────────

export function SchedulingPage() {
  const servicesQuery = useServices()
  const staffQuery = useStaffMembers()
  const businessQuery = useBookingBusiness()
  const createAppointment = useCreateAppointment()
  const bookingSectionRef = useRef<HTMLElement | null>(null)

  // Booking state
  const [selectedService, setSelectedService] = useState<BookingService | null>(null)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null)
  const [month, setMonth] = useState(startOfMonth(new Date()))
  const [selectedTimeZone, setSelectedTimeZone] = useState(() =>
    getInitialBookingTimeZone(GRAPH_CONFIG.bookingsTimeZone),
  )
  const [confirmedAppointment, setConfirmedAppointment] = useState<BookingAppointment | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const form = useForm<DetailsFormValues>({
    resolver: zodResolver(detailsSchema),
    mode: 'onChange',
    defaultValues: {
      ...createEmptyCustomerDetails(),
      questions: {},
    },
  })

  // Derived data
  const eligibleStaff = useMemo(() => {
    const staff = staffQuery.data ?? []
    if (!selectedService) return []
    if (!selectedService.staffMemberIds.length) return staff
    return staff.filter((member) => selectedService.staffMemberIds.includes(member.id))
  }, [selectedService, staffQuery.data])

  const assignedStaff = useMemo(() => {
    if (!selectedSlot) return null
    return (staffQuery.data ?? []).find((m) => m.id === selectedSlot.staffId) ?? null
  }, [selectedSlot, staffQuery.data])

  const availableServices = useMemo(() => servicesQuery.data ?? [], [servicesQuery.data])
  const selectedTimeZoneLabel = useMemo(
    () => BOOKING_TIME_ZONES.find((timeZone) => timeZone.id === selectedTimeZone)?.label ?? selectedTimeZone,
    [selectedTimeZone],
  )

  const availabilityQuery = useAvailability({
    selectedDate,
    selectedService,
    selectedStaff: null,
    eligibleStaff,
    timeZone: selectedTimeZone,
    businessInfo: businessQuery.data ?? null,
    businessInfoReady: !businessQuery.isLoading,
  })

  const serviceQuestions = getServiceQuestions(selectedService)

  // Right panel view derived from state (success is now full-width — handled separately)
  type RightView = 'idle' | 'slots' | 'form'
  const rightView: RightView = selectedSlot ? 'form' : selectedDate ? 'slots' : 'idle'

  useEffect(() => {
    form.setValue('questions', {}, { shouldValidate: true })
  }, [form, selectedService])

  useLayoutEffect(() => {
    if (!confirmedAppointment) return
    bookingSectionRef.current?.scrollIntoView({ block: 'start', inline: 'nearest' })
  }, [confirmedAppointment])

  // ── Actions ──────────────────────────────────────────────────────────────

  const chooseService = (service: BookingService) => {
    setSelectedService(service)
    setSelectedDate(null)
    setSelectedSlot(null)
    setConfirmedAppointment(null)
    setSubmitError(null)
  }

  const changeTimeZone = (timeZone: string) => {
    setSelectedTimeZone(timeZone)
    setSelectedSlot(null)
    setSubmitError(null)
  }

  const handleSubmit = form.handleSubmit(async (values) => {
    if (!selectedService || !selectedSlot) return
    setSubmitError(null)

    try {
      const normalizedPhone = normalizeCustomerPhone(values.phone)
      const bookingsPriceType =
        selectedService.priceType === 'fixedPrice'
          ? 'SERVICEDEFAULTPRICETYPES_FIXED_PRICE'
          : 'SERVICEDEFAULTPRICETYPES_NOT_SET'

      const response = await createAppointment.mutateAsync({
        appointment: {
          startTime: toDateTimeTimeZone(selectedSlot.startDateTime, selectedTimeZone),
          endTime: toDateTimeTimeZone(selectedSlot.endDateTime, selectedTimeZone),
          serviceId: selectedService.id,
          staffMemberIds: [selectedSlot.staffId],
          customers: [
            {
              name: values.name,
              emailAddress: values.email,
              phone: normalizedPhone,
              notes: values.notes || null,
              timeZone: selectedTimeZone,
              answeredCustomQuestions: serviceQuestions.map((question) => ({
                questionId: question.id,
                question: question.displayName,
                answer: values.questions?.[question.id] ?? '',
                selectedOptions:
                  question.answerInputType === 'radioButton' && values.questions?.[question.id]
                    ? [values.questions[question.id]]
                    : [],
                answerInputType: question.answerInputType,
                answerOptions: question.answerOptions,
                isRequired: question.isRequired,
              })),
              location: {
                displayName: '',
                address: { street: '', type: 'Other' },
              },
              smsNotificationsEnabled: false,
              instanceId: '',
              price: selectedService.price ?? 0,
              priceType: bookingsPriceType,
            },
          ],
          isLocationOnline: selectedService.isLocationOnline,
          smsNotificationsEnabled: Boolean(
            selectedService.smsNotificationsEnabled && values.smsOptIn,
          ),
          verificationCode: '',
          customerTimeZone: selectedTimeZone,
          trackingDataId: '',
          bookingFormInfoList: [],
          price: selectedService.price ?? 0,
          priceType: bookingsPriceType,
          isAllDay: false,
          additionalRecipients: [],
        },
      })

      setConfirmedAppointment({
        ...response,
        serviceName: response.serviceName ?? selectedService.displayName,
      })
      const stored = sessionStorage.getItem('br_tracking_data');
      const tracking = stored ? JSON.parse(stored) : {};
      void postContact({
        name: values.name,
        email: values.email,
        phone: normalizedPhone,
        company: values.company,
        message: values.notes ?? '',
        source: tracking.utm_source || 'direct',
        sourceOfContact: 'BOOKING'
      }).catch((err) => {
        console.error('postContact (bookings) failed:', err)
      })
    } catch (error) {
      setSubmitError((error as Error).message)
    }
  })

  const resetAll = () => {
    setSelectedService(null)
    setSelectedDate(null)
    setSelectedSlot(null)
    setConfirmedAppointment(null)
    setSubmitError(null)
    form.reset({ ...createEmptyCustomerDetails(), questions: {} })
  }

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <section ref={bookingSectionRef} className={`contentbox whitebox w100 ${styles.page}`}>
      <div className={styles.threeCol}>

        {/* ── BOOKING CONFIRMED: full-width ticket ── */}
        {confirmedAppointment && (
          <div className={styles.successTicket}>
            <div className={styles.stLeft}>
              <div className={styles.stLeftInner}>
                <div className={styles.stCheckWrap}><CheckmarkCircle24Filled /></div>
                <div className={styles.stConfLabel}>Booking Confirmed</div>
                <div className={styles.stHeading}>You&apos;re<br />booked.</div>
                {selectedDate && (
                  <div className={styles.stDate}>{format(selectedDate, 'EEEE, MMM d, yyyy')}</div>
                )}
              </div>
            </div>

            <div className={styles.stRight}>
              <div className={styles.stRightInner}>
                <div className={styles.stKicker}>Binary Republik</div>
                <div className={styles.stSvcName}>{confirmedAppointment.serviceName}</div>
                <div className={styles.stDivider} />

                {/* Detail card with orange left accent */}
                <div className={styles.stDetailCard}>
                  <div className={styles.stRows}>
                    {selectedDate && (
                      <div className={styles.stRow}>
                        <span className={styles.stRowKey}>Date</span>
                        <span className={styles.stRowVal}>{format(selectedDate, 'EEEE, MMMM d, yyyy')}</span>
                      </div>
                    )}
                    {selectedSlot && (
                      <div className={styles.stRow}>
                        <span className={styles.stRowKey}>Time</span>
                        <span className={styles.stRowVal}>
                          {formatAppointmentDateTime(selectedSlot.startDateTime, selectedSlot.endDateTime, selectedTimeZone)}
                        </span>
                      </div>
                    )}
                    {selectedService && (
                      <div className={styles.stRow}>
                        <span className={styles.stRowKey}>Format</span>
                        <span className={styles.stRowVal}>
                          {formatDuration(selectedService.duration)} · {selectedService.isLocationOnline ? 'Online' : 'In person'}
                        </span>
                      </div>
                    )}
                    {assignedStaff && (
                      <div className={styles.stRow}>
                        <span className={styles.stRowKey}>With</span>
                        <span className={styles.stRowVal}>{assignedStaff.displayName}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.stNote}>
                  A confirmation has been sent to your email.
                </div>

                <div className={styles.stActions}>
                  {confirmedAppointment.joinWebUrl && (
                    <Link className={styles.stJoinLink} href={confirmedAppointment.joinWebUrl}>
                      Join meeting →
                    </Link>
                  )}
                  <button type="button" className={styles.stAgainBtn} onClick={resetAll}>
                    Book another appointment
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── BOOKING FLOW: three-column layout ── */}
        {!confirmedAppointment && <>

        {/* ── LEFT: Services + Staff display + Summary ── */}
        <div className={styles.leftCol}>
          <div className={styles.colHead}>
            <div className={styles.colLabel}>Binary Republik</div>
            <div className={styles.colTitle}>Book a consultation</div>
          </div>

          <div className={styles.colBody}>
            {/* Services */}
            <div className={styles.secLabel}>Service</div>

            {servicesQuery.error ? (
              <ErrorBanner
                title="Unable to load services"
                message={(servicesQuery.error as Error).message}
                actionLabel="Retry"
                onAction={() => { void servicesQuery.refetch() }}
              />
            ) : servicesQuery.isLoading ? (
              <LoadingSpinner message="Loading..." />
            ) : (
              <div className={styles.svcList}>
                {availableServices.map((service) => {
                  const selected = selectedService?.id === service.id
                  return (
                    <button
                      key={service.id}
                      type="button"
                      className={`${styles.svcBtn} ${selected ? styles.svcBtnOn : ''}`}
                      onClick={() => chooseService(service)}
                      title={service.displayName}
                    >
                      <div className={styles.svcBtnLeft}>
                        <div className={styles.svcBtnName} title={service.displayName}>{service.displayName}</div>
                        <div
                          className={styles.svcBtnMeta}
                          title={`${formatDuration(service.duration)} · ${service.isLocationOnline ? 'Online' : 'In person'}`}
                        >
                          {formatDuration(service.duration)} · {service.isLocationOnline ? 'Online' : 'In person'}
                        </div>
                      </div>
                      <div className={`${styles.svcDot} ${selected ? styles.svcDotOn : ''}`} />
                    </button>
                  )
                })}
              </div>
            )}

            {/* Staff display */}
            {selectedService && (
              <>
                <div className={styles.secDivider} />
                <div className={styles.secLabel}>Team</div>
                {staffQuery.isLoading ? (
                  <div className={styles.staffLoading}>Loading team…</div>
                ) : eligibleStaff.length ? (
                  <div className={styles.staffList}>
                    {eligibleStaff.map((member) => (
                      <div key={member.id} className={styles.staffChip} title={member.displayName}>
                        <span className={styles.staffInitial}>{getInitials(member.displayName)}</span>
                        <span className={styles.staffName}>{member.displayName}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.staffLoading}>No team assigned</div>
                )}
              </>
            )}

          </div>
        </div>

        {/* ── CENTER: Calendar ── */}
        <div className={styles.centerCol}>
          <div className={styles.colHead}>
            <div className={styles.colLabel}>Availability</div>
            <div className={styles.colTitle}>
              {selectedDate ? format(selectedDate, 'MMMM yyyy') : format(month, 'MMMM yyyy')}
            </div>
          </div>

          <div className={styles.colBody}>
            {/* Timezone */}
            <div className={styles.tzRow}>
              <span className={styles.tzLabel}>TZ</span>
              <select
                className={styles.tzSelect}
                value={selectedTimeZone}
                onChange={(e) => changeTimeZone(e.target.value)}
                title={selectedTimeZoneLabel}
              >
                {BOOKING_TIME_ZONES.map((tz) => (
                  <option key={tz.id} value={tz.id}>{tz.label}</option>
                ))}
              </select>
            </div>

            <CalendarPicker
              month={month}
              selectedDate={selectedDate}
              onMonthChange={setMonth}
              onSelectDate={(date) => {
                setSelectedDate(date)
                setSelectedSlot(null)
                setSubmitError(null)
              }}
              maximumAdvance={selectedService?.schedulingPolicy?.maximumAdvance ?? null}
              openDays={
                businessQuery.data
                  ? getBusinessOpenDays(businessQuery.data.businessHours)
                  : undefined
              }
            />

            {!selectedService && (
              <div className={styles.calHint}>← Select a service first</div>
            )}
          </div>
        </div>

        {/* ── RIGHT: Idle → Slots → Form → Success ── */}
        <div className={styles.rightCol}>

          {/* Idle */}
          {rightView === 'idle' && (
            <>
              <div className={styles.colHead}>
                <div className={styles.colLabel}>Available times</div>
                <div className={styles.colTitle}>Select a date</div>
              </div>
              <div className={styles.colBody}>
                <div className={styles.idleMsg}>
                  <div className={styles.idleIcon}><CalendarLtr24Regular /></div>
                  <div>Pick a date from the calendar to see available times.</div>
                </div>
              </div>
            </>
          )}

          {/* Slots */}
          {rightView === 'slots' && (
            <>
              <div className={styles.colHead}>
                <div className={styles.colLabel}>Available times</div>
                <div className={styles.colTitle}>
                  {selectedDate ? format(selectedDate, 'EEEE, MMM d') : ''}
                </div>
              </div>
              <div className={styles.colBody}>
                {availabilityQuery.error ? (
                  <ErrorBanner
                    title="Unable to load availability"
                    message={(availabilityQuery.error as Error).message}
                    actionLabel="Retry"
                    onAction={() => { void availabilityQuery.refetch() }}
                  />
                ) : (
                  <TimeSlotGrid
                    hasSelectedDate={Boolean(selectedDate)}
                    slots={availabilityQuery.data ?? []}
                    isLoading={availabilityQuery.isFetching}
                    selectedSlot={selectedSlot}
                    onSelectSlot={(slot) => {
                      setSelectedSlot(slot)
                      setSubmitError(null)
                    }}
                  />
                )}
              </div>
            </>
          )}

          {/* Form */}
          {rightView === 'form' && (
            <>
              <div className={styles.colHead}>
                <div className={styles.colLabel}>Your details</div>
                <div className={styles.colTitle}>Confirm booking</div>
              </div>
              <div className={styles.colBody}>
                <form onSubmit={handleSubmit} className={styles.detailsForm}>
                  <Controller
                    name="name"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Full name"
                        margin="normal"
                        variant="standard"
                        className="formInput"
                        value={field.value ?? ''}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message ?? ''}
                      />
                    )}
                  />

                  <Controller
                    name="email"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Email"
                        margin="normal"
                        variant="standard"
                        className="formInput"
                        value={field.value ?? ''}
                        type="email"
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message ?? ''}
                      />
                    )}
                  />

                  <Controller
                    name="phone"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Contact No"
                        margin="normal"
                        variant="standard"
                        className="formInput"
                        value={field.value ?? ''}
                        type="tel"
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message ?? ''}
                      />
                    )}
                  />

                  <Controller
                    name="company"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Company Name"
                        margin="normal"
                        variant="standard"
                        className="formInput"
                        value={field.value ?? ''}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message ?? ''}
                      />
                    )}
                  />

                  <Controller
                    name="notes"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Notes (optional)"
                        margin="normal"
                        variant="standard"
                        className="formInput"
                        value={field.value ?? ''}
                        multiline
                        rows={2}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message ?? ''}
                      />
                    )}
                  />

                  {serviceQuestions.map((question) => (
                    <div key={question.id} className={styles.fullWidth}>
                      <Controller
                        name={`questions.${question.id}`}
                        control={form.control}
                        rules={{
                          required: question.isRequired ? 'This field is required' : false,
                        }}
                        render={({ field, fieldState }) => (
                          <FormControl
                            className={styles.fullWidth}
                            error={Boolean(fieldState.error)}
                            required={question.isRequired}
                            variant="standard"
                          >
                            <FormLabel>{question.displayName}</FormLabel>
                            {question.answerInputType === 'radioButton' ? (
                              <RadioGroup
                                value={field.value ?? ''}
                                onChange={(e) => field.onChange(e.target.value)}
                              >
                                {question.answerOptions.map((option) => (
                                  <FormControlLabel
                                    key={option}
                                    value={option}
                                    control={<Radio />}
                                    label={option}
                                  />
                                ))}
                              </RadioGroup>
                            ) : (
                              <TextField
                                fullWidth
                                margin="normal"
                                variant="standard"
                                className="formInput"
                                value={field.value ?? ''}
                                onChange={(e) => field.onChange(e.target.value)}
                              />
                            )}
                            {fieldState.error?.message ? (
                              <Typography component="p" color="error" variant="caption">
                                {fieldState.error.message}
                              </Typography>
                            ) : null}
                          </FormControl>
                        )}
                      />
                    </div>
                  ))}

                  {hasSmsSupport(selectedService) ? (
                    <Controller
                      name="smsOptIn"
                      control={form.control}
                      render={({ field }) => (
                        <FormControlLabel
                          className={styles.checkboxControl}
                          control={
                            <MuiCheckbox
                              checked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                            />
                          }
                          label="Text message updates"
                        />
                      )}
                    />
                  ) : null}

                  {(selectedService || selectedDate || selectedSlot) && (
                    <div className={styles.summaryBlock}>
                      {selectedService && (
                        <div className={styles.sumRow}>
                          <span className={styles.sumKey}>Service</span>
                          <span
                            className={`${styles.sumVal} ${styles.sumValHi}`}
                            title={selectedService.displayName}
                          >
                            {selectedService.displayName}
                          </span>
                        </div>
                      )}
                      {selectedDate && (
                        <div className={styles.sumRow}>
                          <span className={styles.sumKey}>Date</span>
                          <span
                            className={styles.sumVal}
                            title={format(selectedDate, 'EEEE, MMM d, yyyy')}
                          >
                            {format(selectedDate, 'EEE, MMM d')}
                          </span>
                        </div>
                      )}
                      {selectedSlot && (
                        <div className={styles.sumRow}>
                          <span className={styles.sumKey}>Time</span>
                          <span
                            className={styles.sumVal}
                            title={formatAppointmentDateTime(
                              selectedSlot.startDateTime,
                              selectedSlot.endDateTime,
                              selectedTimeZone,
                            )}
                          >
                            {format(selectedSlot.startDateTime, 'h:mm a')}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {submitError ? (
                    <div className={styles.errorWrap}>
                      <ErrorBanner title="Unable to confirm booking" message={submitError} />
                    </div>
                  ) : null}

                  <MuiButton
                    className={styles.confirmBtn}
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={!form.formState.isValid || createAppointment.isPending}
                    disableElevation
                    fullWidth
                  >
                    {createAppointment.isPending ? (
                      <span className={styles.submitButtonContent}>
                        <CircularProgress size={16} style={{ color: '#fff' }} />
                        <span>Booking…</span>
                      </span>
                    ) : (
                      'Confirm booking'
                    )}
                  </MuiButton>

                  <button
                    type="button"
                    className={styles.backLink}
                    onClick={() => setSelectedSlot(null)}
                  >
                    ← Choose a different time
                  </button>
                </form>
              </div>
            </>
          )}

        </div>

        </>}
      </div>
    </section>
  )
}
